import { NavLink, useLocation } from "react-router-dom";
import { Home, Plus, BarChart3, Upload, ClipboardList } from 'lucide-react';
import { useUser } from '../../context/UserContext';
import { useComptesGarEnAttente } from '../../hooks/fetch_api_hook/useComptesGarEnAttente';
import {
    navItemClasses,
    subNavItemClasses,
    navItemStyle,
    ActiveBar,
    iconClasses,
} from './sidebarStyles';

function SideBarProfCordo({ handleHomeClick }) {
    const { pathname } = useLocation();
    const { etablissementData, user } = useUser();

    // Import établissement : la seule porte de création/mise à jour des élèves,
    // réservée au délégué d'affectation (le chef a la sienne dans SideBarChef).
    const isDelegue = !!etablissementData?.professeurs?.find(p => p.id === user?.id)?.delegue_affectation;
    const isImportActive = pathname.includes('import-etablissement');
    // Badge « comptes GAR en attente d'import » (filtré périmètre côté back)
    const { data: enAttente } = useComptesGarEnAttente(
        etablissementData?.id, !!etablissementData?.is_gar && isDelegue);

    // CTA « Créer un devoir commun » : surligné sur la page de création.
    const isCreerDevoirActive = pathname.includes('add-common-eval');

    // Liste des devoirs communs (colonne Affectation) : avant cette entrée,
    // la page n'était joignable QU'À la sortie de la création (ou par URL).
    // Exclut add-common-eval pour ne pas allumer les deux items à la fois.
    const isDevoirsCommunsActive = pathname.includes('common-eval') && !isCreerDevoirActive;

    // « Pilotage » : UN item → page à onglets ; reste surligné sur les 4 vues
    // coordo (même principe que le Pilotage de classe, cf. SideBarProf). On matche
    // `progression` (sans idclasse) + les 3 routes coordo.
    const isPilotageActive =
        /\/home\/progression\/?$/.test(pathname) ||
        ['admin-groupe', 'groupes-common', 'eval-nat-niveau'].some((r) => pathname.includes(r));

    return (
        <>
            {/* Accueil */}
            <div className="flex-1 px-3 py-2">
                <NavLink
                    to="/home/"
                    onClick={handleHomeClick}
                    end
                    className={navItemClasses}
                    style={navItemStyle}
                >
                    {({ isActive }) => (
                        <>
                            {isActive && <ActiveBar />}
                            <Home size={18} className={iconClasses(isActive)} />
                            <span className="text-[14px] font-medium leading-tight flex-1">Accueil</span>
                        </>
                    )}
                </NavLink>
            </div>

            {/* Section Coordonnateur — même système de design que « Réglages »
                (SideBarProf) : en-tête discret + items imbriqués (bordure gauche). */}
            <div className="px-6 pt-3 pb-1">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-200/60">Coordonnateur</span>
            </div>
            <ul>
                {/* Créer un devoir commun — CTA du parcours coordo, même style que
                    « Créer une évaluation » de SideBarProf (encart cyan + icône +). */}
                <li className="ml-6 pl-3 border-l border-blue-300/30 py-1.5 list-none">
                    <NavLink
                        to="add-common-eval"
                        className={() =>
                            isCreerDevoirActive
                                ? navItemClasses({ isActive: true })
                                : 'group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 text-white bg-white/10 border border-cyan-300/40 hover:bg-white/20'
                        }
                        style={() => navItemStyle({ isActive: isCreerDevoirActive })}
                    >
                        {isCreerDevoirActive && <ActiveBar />}
                        <Plus size={18} className={isCreerDevoirActive ? iconClasses(true) : 'shrink-0 text-cyan-200'} />
                        <span className="text-[14px] font-medium leading-tight flex-1">Créer un devoir commun</span>
                    </NavLink>
                </li>

                {/* Devoirs communs — la LISTE (suivi des affectations de saisie) */}
                <li className="ml-6 pl-3 border-l border-blue-300/30 py-1.5 list-none">
                    <NavLink
                        to="common-eval"
                        className={() => subNavItemClasses({ isActive: isDevoirsCommunsActive })}
                        style={() => navItemStyle({ isActive: isDevoirsCommunsActive })}
                    >
                        {isDevoirsCommunsActive && <ActiveBar />}
                        <ClipboardList size={16} className={iconClasses(isDevoirsCommunsActive)} />
                        <span className="text-[13px] font-medium leading-tight flex-1">Devoirs communs</span>
                    </NavLink>
                </li>

                {/* Pilotage — un seul item → page à onglets (Programmation commune,
                    Configuration des groupes, Répartition par résultats, Résultats
                    nationaux). Reste actif sur l'ensemble des routes coordo. */}
                <li className="ml-6 pl-3 border-l border-blue-300/30 py-1.5 list-none">
                    <NavLink
                        to="progression"
                        className={() => subNavItemClasses({ isActive: isPilotageActive })}
                        style={() => navItemStyle({ isActive: isPilotageActive })}
                    >
                        {isPilotageActive && <ActiveBar />}
                        <BarChart3 size={16} className={iconClasses(isPilotageActive)} />
                        <span className="text-[13px] font-medium leading-tight flex-1">Pilotage</span>
                    </NavLink>
                </li>
            </ul>

            {/* Section Délégué — import établissement (wizard unique, spec GAR §10) */}
            {isDelegue ? (
                <>
                    <div className="px-6 pt-3 pb-1">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-200/60">Délégué</span>
                    </div>
                    <ul>
                        <li className="ml-6 pl-3 border-l border-blue-300/30 py-1.5 list-none">
                            <NavLink
                                to="import-etablissement"
                                className={() => subNavItemClasses({ isActive: isImportActive })}
                                style={() => navItemStyle({ isActive: isImportActive })}
                            >
                                {isImportActive && <ActiveBar />}
                                <Upload size={16} className={iconClasses(isImportActive)} />
                                <span className="text-[13px] font-medium leading-tight flex-1">Import des élèves</span>
                                {enAttente?.count > 0 ? (
                                    <span
                                        className="min-w-[18px] h-[18px] inline-flex items-center justify-center px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold"
                                        title={`${enAttente.count} élève(s) connecté(s) via l'ENT sans fiche — en attente d'import ou d'arbitrage`}
                                    >
                                        {enAttente.count}
                                    </span>
                                ) : null}
                            </NavLink>
                        </li>
                    </ul>
                </>
            ) : null}
        </>
    );
}

export default SideBarProfCordo;
